import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PhoneInput from './PhoneInput';
import {
  getLabelStyle as fsLabel,
  getInputStyle as fsInput,
  getTextareaStyle as fsTextarea,
  getRequiredMarkStyle as fsRequired,
  getSubmitStyle,
  getSubmitWrapStyle,
} from '../pages/LandingPageBuilder/components/formStyles';

const API_BASE = '/api';

interface Props {
  slug: string;            // campaign / tool slug
  blockId: string;
  p: Record<string, any>;  // block props (server re-reads its own copy on submit)
  baseStyle?: React.CSSProperties;
  // Which public host serves this block — campaign pages (/campaign/:slug,
  // default) or landing-page tools (/p/:slug). Selects the API namespace.
  apiKind?: 'campaigns' | 'tools';
}

interface TraineeRegConfig {
  title: string;
  subtitle: string;
  submitText: string;
  successMessage: string;
  groupMode: 'choice' | 'fixed';
  groups: { _id: string; name: string; activity: string; location: string }[];
  fields: { birthDate: boolean; idNumber: boolean; gender: boolean; parent: boolean; medical: boolean; notes: boolean };
}

/**
 * Public trainee-registration form (בלוק רישום מתאמן): fetches the block's
 * server-side config (group list + field toggles), collects trainee (+
 * optional parent) details, and submits a PENDING training registration.
 * Simpler sibling of StudentRegistrationBlock — no payment, no masav, no
 * two-step class select.
 */
const TraineeRegistrationBlock: React.FC<Props> = ({ slug, blockId, p, baseStyle = {}, apiKind = 'campaigns' }) => {
  const [config, setConfig] = useState<TraineeRegConfig | null>(null);
  const [configErr, setConfigErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<{ message: string } | null>(null);

  const [trainee, setTrainee] = useState({ firstName: '', lastName: '', phone: '', email: '', idNumber: '', birthDate: '', gender: '' });
  const [parent, setParent] = useState({ name: '', phone: '' });
  const [medicalNotes, setMedicalNotes] = useState('');
  const [notes, setNotes] = useState('');
  const [groupId, setGroupId] = useState('');
  const [honeypot, setHoneypot] = useState('');

  useEffect(() => {
    axios.get(`${API_BASE}/public/${apiKind}/${slug}/training-registration-config`, { params: { blockId } })
      .then(r => setConfig(r.data))
      .catch(e => setConfigErr(e?.response?.data?.error || 'הטופס אינו זמין כרגע'));
  }, [slug, blockId, apiKind]);

  const groups = config?.groups || [];
  const fields = config?.fields || { birthDate: true, idNumber: false, gender: false, parent: true, medical: false, notes: false };
  // group select only when the trainee actually has a choice; a single group
  // is auto-selected so the submission still carries it.
  const showGroupSelect = config?.groupMode === 'choice' && groups.length > 1;
  useEffect(() => {
    if (config && config.groupMode === 'choice' && groups.length === 1) setGroupId(groups[0]._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const idInput = (value: string, onChange: (v: string) => void, placeholder: string) => {
    const invalid = !!value && !/^\d{9}$/.test(value);
    return <input type="text" inputMode="numeric" dir="ltr" maxLength={9} placeholder={placeholder} value={value}
      onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 9))}
      style={{ ...fsInput(p, {} as any), ...(invalid ? { borderColor: '#dc2626' } : {}) }} />;
  };

  const label = (text: string, required?: boolean) => (
    <label style={fsLabel(p, {} as any)}>{text}{required && <span style={fsRequired(p)}>*</span>}</label>
  );

  const submit = async () => {
    setError('');
    if (!trainee.firstName.trim()) { setError('נא למלא שם פרטי'); return; }
    if (!trainee.phone.trim() && !parent.phone.trim()) { setError('נדרש מספר טלפון'); return; }
    if (trainee.idNumber && !/^\d{9}$/.test(trainee.idNumber)) { setError('מספר תעודת הזהות אינו תקין'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/${apiKind}/${slug}/register-trainee`, {
        blockId,
        website: honeypot,
        groupId: groupId || undefined,
        trainee: {
          firstName: trainee.firstName,
          lastName: trainee.lastName,
          phone: trainee.phone,
          email: trainee.email,
          idNumber: trainee.idNumber,
          birthDate: trainee.birthDate || undefined,
          gender: trainee.gender,
        },
        parent: { name: parent.name, phone: parent.phone },
        medicalNotes,
        notes,
      });
      setDone({ message: data?.message || config?.successMessage || 'ההרשמה התקבלה!' });
    } catch (e: any) {
      setError(e?.response?.data?.error || 'שגיאה בשליחת ההרשמה — נסו שוב');
    } finally {
      setSubmitting(false);
    }
  };

  const card: React.CSSProperties = {
    ...baseStyle,
    backgroundColor: baseStyle.backgroundColor || '#fff',
    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    overflowY: 'auto',
  };

  if (done) {
    return (
      <div style={{ ...card, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, textAlign: 'center', padding: 24 }} dir="rtl">
        <div style={{ fontSize: 44 }}>✓</div>
        <div style={{ fontSize: 18, fontWeight: 800 }}>{done.message}</div>
      </div>
    );
  }

  if (configErr) {
    return <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 14 }} dir="rtl">{configErr}</div>;
  }

  const gap = p.fieldGap ?? 12;
  return (
    <div style={card} dir="rtl">
      <form style={{ display: 'flex', flexDirection: 'column', gap }} onSubmit={e => { e.preventDefault(); submit(); }}>
        <div style={{ fontSize: 20, fontWeight: 800 }}>
          {config?.title || p.title || 'הרשמה לקבוצת אימון'}
          {config?.subtitle ? <div style={{ fontWeight: 500, color: '#6b7280', fontSize: 14 }}>{config.subtitle}</div> : null}
        </div>

        {showGroupSelect && (
          <div>{label('קבוצה')}
            <select value={groupId} onChange={e => setGroupId(e.target.value)} style={fsInput(p, {} as any)}>
              <option value="">בחר...</option>
              {groups.map(g => <option key={g._id} value={g._id}>{g.name}{g.activity ? ` · ${g.activity}` : ''}</option>)}
            </select>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap }}>
          <div>{label('שם פרטי', true)}<input type="text" value={trainee.firstName} onChange={e => setTrainee(s => ({ ...s, firstName: e.target.value }))} style={fsInput(p, {} as any)} /></div>
          <div>{label('שם משפחה')}<input type="text" value={trainee.lastName} onChange={e => setTrainee(s => ({ ...s, lastName: e.target.value }))} style={fsInput(p, {} as any)} /></div>
        </div>
        <div>{label('טלפון')}<PhoneInput value={trainee.phone} onChange={v => setTrainee(s => ({ ...s, phone: v }))} style={fsInput(p, {} as any)} dir="rtl" /></div>
        <div>{label('אימייל')}<input type="email" dir="ltr" value={trainee.email} onChange={e => setTrainee(s => ({ ...s, email: e.target.value }))} style={fsInput(p, {} as any)} /></div>

        {fields.birthDate && <div>{label('תאריך לידה')}<input type="date" dir="ltr" value={trainee.birthDate} onChange={e => setTrainee(s => ({ ...s, birthDate: e.target.value }))} style={fsInput(p, {} as any)} /></div>}
        {fields.idNumber && <div>{label('תעודת זהות')}{idInput(trainee.idNumber, v => setTrainee(s => ({ ...s, idNumber: v })), 'תעודת זהות (9 ספרות)')}</div>}
        {fields.gender && (
          <div>{label('מגדר')}
            <select value={trainee.gender} onChange={e => setTrainee(s => ({ ...s, gender: e.target.value }))} style={fsInput(p, {} as any)}>
              <option value="">בחר...</option><option value="זכר">זכר</option><option value="נקבה">נקבה</option>
            </select>
          </div>
        )}

        {fields.parent && (
          <>
            <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 8, fontWeight: 700, fontSize: 14 }}>פרטי הורה (לקטינים)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap }}>
              <div>{label('שם הורה')}<input type="text" value={parent.name} onChange={e => setParent(x => ({ ...x, name: e.target.value }))} style={fsInput(p, {} as any)} /></div>
              <div>{label('טלפון הורה')}<PhoneInput value={parent.phone} onChange={v => setParent(x => ({ ...x, phone: v }))} style={fsInput(p, {} as any)} dir="rtl" /></div>
            </div>
          </>
        )}

        {fields.medical && <div>{label('מידע רפואי שחשוב שנדע')}<textarea rows={2} value={medicalNotes} onChange={e => setMedicalNotes(e.target.value)} style={fsTextarea(p, {} as any)} /></div>}
        {fields.notes && <div>{label('הערות')}<textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} style={fsTextarea(p, {} as any)} /></div>}

        {/* honeypot — invisible to humans, bots fill it */}
        <input type="text" value={honeypot} onChange={e => setHoneypot(e.target.value)} autoComplete="off" tabIndex={-1}
          style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }} aria-hidden="true" name="website" />

        {error && <div style={{ color: '#dc2626', fontSize: 13, fontWeight: 600 }}>{error}</div>}

        <div style={getSubmitWrapStyle(p)}>
          <button type="submit" disabled={submitting} style={{ ...getSubmitStyle(p), opacity: submitting ? 0.6 : 1 }}>
            {submitting ? 'שולח...' : (config?.submitText || p.submitText || 'הירשם')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TraineeRegistrationBlock;
